import { Close } from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Drawer } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useRef, useState } from 'react'
import { I18n, Translate } from 'src/i18n'
import { useListsStore } from 'src/store/lists/ListsStore'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import { zIndex } from 'src/theme/zIndex'
import { Button as DeleteButton } from 'src/ui-components'
import styled from 'styled-components'
import ListNode from '../../../../../store/lists/ListNode'
import { FlatGroup } from '../../../../../store/lists/interfaces'
import CollapsibleGroup from './CollapsibleGroup'

export const DeleteItemsFromListsModal = observer((): ReactElement => {
  const ref = useRef<HTMLInputElement>(null)
  const listStore = useListsStore()

  const [loading, setLoading] = useState<boolean>(false)

  const {
    showDeleteItemsFromListsModal,
    setShowDeleteItemsFromListsModal,
    onDeleteGroupPart,
  } = listStore

  const handleOnClose = useCallback(() => {
    setShowDeleteItemsFromListsModal(false)
  }, [setShowDeleteItemsFromListsModal])

  const deleteItems = listStore.getSelectedPartsBasket()?.items
  const selectedPartsCount = listStore.getAllSelectedParts()?.length

  const handleDeleteGroupParts = () => {
    setLoading(true)
    const itemsToDelete = listStore.getAllSelectedParts()
    const deletePartIds = itemsToDelete.map((parts) => {
      return parts.id
    })

    onDeleteGroupPart(deletePartIds)
      .then(() => {
        listStore.clearAllSelections()
        handleOnClose()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getParentCategory = (listId: number): ListNode => {
    const category = listStore.root
      .getChildren()
      .find((c) => c.getChildren().find((i) => i.id === listId))

    return category
  }

  const getFlatGroups = (): FlatGroup[] => {
    if (!deleteItems?.length) return []

    return deleteItems.flatMap((item) => {
      const category = getParentCategory(item.listId)
      const list = category?.getChildren().find((l) => l.id === item.listId)
      const flattenGroups = item.groups?.map((group) => {
        const groupItem = list?.getChildren().find((g) => g.id === group.id)

        return {
          category: category?.value ?? '',
          list: list?.value ?? '',
          group: groupItem?.value ?? '',
          parts: group.parts,
        }
      })

      return flattenGroups
    })
  }

  const rows = getFlatGroups()

  const partText = selectedPartsCount > 1 ? 'parts' : 'part'
  const deleteButtonText = `${Translate('delete')} ${selectedPartsCount} ${Translate(partText)}`

  return (
    <Drawer
      anchor="right"
      open={showDeleteItemsFromListsModal}
      onClose={handleOnClose}
      style={{ width: '854px', zIndex: zIndex.modal }}
    >
      <Container ref={ref}>
        <Box
          height="100%"
          display="flex"
          justifyContent="space-between"
          flexDirection="column"
        >
          <TitleHeader>
            <Title>
              <I18n name="deleteItemFromList" />
              {'\u00a0'}
              <ItemsAmount>({selectedPartsCount} items)</ItemsAmount>
            </Title>
            <StyledCloseIcon onClick={handleOnClose} />
          </TitleHeader>

          <GroupsContainer>
            {rows?.map((r, idx) => (
              <CollapsibleGroup key={`cat_list_group_row_${idx}`} {...r} />
            ))}
          </GroupsContainer>

          <ButtonContainer>
            <ButtonCancel onClick={handleOnClose}>
              <I18n name="cancel" />
            </ButtonCancel>

            {selectedPartsCount > 0 && (
              <DeleteButton
                type="button"
                disabled={loading}
                id="delete-button"
                text={deleteButtonText}
                onClick={() => handleDeleteGroupParts()}
                variant="danger"
                loading={loading}
                icon={<StyledDeleteIcon />}
              />
            )}
          </ButtonContainer>
        </Box>
      </Container>
    </Drawer>
  )
})

const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: baseline;
  padding: 12px 24px;
  gap: 9px;
  width: 98%;
  height: 48px;
  background: ${defaultTheme.colors.red};
  border-radius: 3px;
  border: none;

  @media screen and (min-width: ${breakpoints.laptop}px) {
    height: 36px;
    width: 179px;
    flex-direction: row;
    gap: 0px;
    padding: 0px;
    align-items: center;
    margin: 0px;
  }
`
const StyledDeleteIcon = styled(DeleteIcon)`
  cursor: pointer;

  margin: 2px 9px 3px 0px;
  color: ${defaultTheme.colors.secondary};

  @media screen and (min-width: ${breakpoints.laptop}px) {
    height: 22px;
    width: 22px;
  }
`

const ButtonCancel = styled(Button)`
  background-color: white;
  border-radius: 3px;
  border: 1px solid ${defaultTheme.colors.primaryText};
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${defaultTheme.colors.primaryText};
  margin-top: 16px;
  text-align: center;
  text-transform: uppercase;

  @media screen and (min-width: ${breakpoints.laptop}px) {
    margin: 0px;
    width: 91px;
    height: 36px;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  box-shadow: 0px -4px 8px rgba(0, 0, 0, 0.04);

  @media screen and (min-width: ${breakpoints.laptop}px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    box-shadow: none;
    gap: 16px;
    margin-top: 47px;
  }
`

const Container = styled.div`
  height: 100%;
  width: 854px;

  background-color: ${defaultTheme.colors.componentsBackground};
  z-index: ${zIndex.modal};
  padding: 24px;
  overflow-y: auto;
`

const GroupsContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  gap: 20px;
`

const Title = styled.div`
  font-size: 28px;
`

const ItemsAmount = styled.span`
  color: ${defaultTheme.colors.columnBorder};
  font-weight: 400;
`

const StyledCloseIcon = styled(Close)`
  cursor: pointer;
`
const TitleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: Oswald, serif;
  font-style: normal;
  font-weight: 700;
  font-size: 28px;
  line-height: 48px;
  color: ${defaultTheme.colors.almostBlack};
  padding-top: 24px;
  margin-bottom: 18px;
`
